

import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowRight, CalendarIcon, Edit, Plus, Trash2, MapPin, ArrowUp, ArrowDown, StickyNote, Hotel, Search } from 'lucide-react';
import type { ItineraryDay, Note } from '@/app/lib/types';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { useState, useRef, useEffect } from 'react';
import { ActivityEditor } from './ActivityEditor';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { ActivityIcon } from './ActivityIcon';
import { LinkPreview } from './LinkPreview';
import { Input } from '../ui/input';
import { StickyNoteCard } from './StickyNoteCard';
import { Separator } from '../ui/separator';

interface ItineraryDayViewProps {
    dayData?: ItineraryDay;
    onDateChange?: (newDate: Date) => void;
    onUpdateActivity: (dayNumber: number, activityIndex: number, updatedActivity: ItineraryDay['activities'][0]) => void;
    onDeleteActivity: (dayNumber: number, activityIndex: number) => void;
    onAddActivity: (dayNumber: number, newActivity: ItineraryDay['activities'][0]) => void;
    onReorderActivity: (dayNumber: number, activityIndex: number, direction: 'up' | 'down') => void;
    onUpdateDayTitle: (dayNumber: number, newTitle: string) => void;
    onUpdateDayNotes: (dayNumber: number, updatedNotes: Note[]) => void;
    isEditable?: boolean;
}

function isUrl(text: string): boolean {
    if (!text) return false;
    try {
        const url = new URL(text);
        return url.protocol === 'http:' || url.protocol === 'https:';
    } catch (_) {
        return false;
    }
}

type AccommodationAnswer = 'yes' | 'no' | null;

export function ItineraryDayView({
    dayData,
    onDateChange,
    onUpdateActivity,
    onDeleteActivity,
    onAddActivity,
    onReorderActivity,
    onUpdateDayTitle,
    onUpdateDayNotes,
    isEditable = false
}: ItineraryDayViewProps) {
    const [newDate, setNewDate] = useState<Date | undefined>(dayData?.date ? parseISO(dayData.date) : undefined);
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [editingTitle, setEditingTitle] = useState(dayData?.title || '');
    const titleInputRef = useRef<HTMLInputElement>(null);
    const [accommodationAnswer, setAccommodationAnswer] = useState<AccommodationAnswer>(null);


    useEffect(() => {
        if (dayData) {
            setEditingTitle(dayData.title);
            setAccommodationAnswer(null); // Reset answer when day changes
        }
    }, [dayData]);

    useEffect(() => {
        if (isEditingTitle && titleInputRef.current) {
            titleInputRef.current.focus();
            titleInputRef.current.select();
        }
    }, [isEditingTitle]);

    if (!dayData) {
        return (
            <Card>
                <CardHeader>
                    <CardTitle>No day selected</CardTitle>
                    <CardDescription>Please select a day from the plan.</CardDescription>
                </CardHeader>
            </Card>
        );
    }

    if (!dayData.date) {
        return (
            <Card>
                <CardHeader>
                    <CardTitle>{dayData.title}</CardTitle>
                    <CardDescription>Date not available for this day.</CardDescription>
                </CardHeader>
            </Card>
        )
    }
    const dayDate = parseISO(dayData.date);

    const hasLodging = dayData.activities.some(act => act.category === 'lodging');

    const handleDateChange = (selectedDate: Date | undefined) => {
        if (selectedDate && onDateChange) {
            setNewDate(selectedDate);
            onDateChange(selectedDate);
        }
    }

    const handleTitleSave = () => {
        if (editingTitle.trim() !== '' && dayData) {
            onUpdateDayTitle(dayData.day, editingTitle);
        }
        setIsEditingTitle(false);
    };

    const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleTitleSave();
        }
        if (e.key === 'Escape') {
            setEditingTitle(dayData.title);
            setIsEditingTitle(false);
        }
    };

    const dayNotes = dayData.notes || [];

    const handleUpdateNote = (noteId: string, content: string) => {
        const updatedNotes = dayNotes.map(note =>
            note.id === noteId ? { ...note, content } : note
        );
        onUpdateDayNotes(dayData.day, updatedNotes);
    };

    const handleAddNote = () => {
        const newNote: Note = {
            id: crypto.randomUUID(),
            content: 'New dispatch...',
        };
        const updatedNotes = [...dayNotes, newNote];
        onUpdateDayNotes(dayData.day, updatedNotes);
    };

    const handleDeleteNote = (noteId: string) => {
        const updatedNotes = dayNotes.filter(note => note.id !== noteId);
        onUpdateDayNotes(dayData.day, updatedNotes);
    };

    const DayNotesEditor = (
        <div className="mt-8">
            <Separator className="mb-6" />
            <div className="pt-2">
                <CardTitle className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-2">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-accent/20 to-accent/10 flex items-center justify-center ring-2 ring-accent/20">
                            <StickyNote className="h-5 w-5 text-accent" />
                        </div>
                        <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">Daily Dispatches</span>
                    </div>
                    <Button size="sm" variant="outline" onClick={handleAddNote} className="hover:border-accent hover:bg-accent/5 transition-smooth">
                        <Plus className="h-4 w-4 mr-2" /> Add Dispatch
                    </Button>
                </CardTitle>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {dayNotes.length > 0 ? (
                        dayNotes.map(note => (
                            <StickyNoteCard
                                key={note.id}
                                note={note}
                                onUpdate={handleUpdateNote}
                                onDelete={handleDeleteNote}
                            />
                        ))
                    ) : (
                        <div className="col-span-full text-center text-muted-foreground py-12 bg-muted/30 rounded-xl border-2 border-dashed border-muted-foreground/20">
                            <StickyNote className="h-12 w-12 mx-auto mb-3 opacity-40" />
                            <p>No dispatches for this day yet.</p>
                            <p className="text-xs mt-1">Add your first dispatch to get started!</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    const ActivityListContent = (
        <div className="w-full space-y-1">
            {dayData.activities.map((activity, index) => (
                <Accordion type="single" collapsible key={`${dayData.day}-${index}`} className="w-full">
                    <AccordionItem value={`item-${index}`} className="border-b-0 rounded-lg hover:bg-accent/5 transition-smooth group">
                        <div className="flex items-center w-full">
                            {isEditable && (
                                <div className="flex flex-col border-r pr-2 mr-2">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7 hover:bg-primary/10 hover:text-primary"
                                        onClick={() => onReorderActivity(dayData.day, index, 'up')}
                                        disabled={index === 0}
                                    >
                                        <ArrowUp className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7 hover:bg-primary/10 hover:text-primary"
                                        onClick={() => onReorderActivity(dayData.day, index, 'down')}
                                        disabled={index === dayData.activities.length - 1}
                                    >
                                        <ArrowDown className="h-4 w-4" />
                                    </Button>
                                </div>
                            )}
                            <AccordionTrigger className="flex-grow hover:no-underline py-4">
                                <div className="flex items-center gap-3 sm:gap-4 text-left">
                                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center ring-2 ring-primary/10 group-hover:ring-accent/30 transition-smooth">
                                        <ActivityIcon category={activity.category} className="h-5 w-5 text-primary" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="font-semibold text-base">{activity.time}</p>
                                        <p className="text-sm text-muted-foreground">{activity.locationName}</p>
                                    </div>
                                </div>
                            </AccordionTrigger>
                        </div>
                        <AccordionContent className="prose prose-sm max-w-none text-foreground pl-4 sm:pl-16 pr-4 pb-4 space-y-4 border-l-2 border-accent/20 ml-5 sm:ml-2">
                            {activity.description && isUrl(activity.description) ? (
                                <LinkPreview url={activity.description} />
                            ) : (
                                <p className="text-foreground/90">{activity.description}</p>
                            )}
                            <div className="flex items-center justify-between mt-4 pt-4 border-t border-border/50">
                                <div className="flex items-center gap-2 flex-wrap">
                                    {activity.description && isUrl(activity.description) ? (
                                        <Button asChild variant="link" size="sm" className="pl-0 text-primary hover:text-accent">
                                            <a href={activity.description} target="_blank" rel="noopener noreferrer">
                                                Explore Link <ArrowRight className="ml-2 h-4 w-4" />
                                            </a>
                                        </Button>
                                    ) : activity.locationSlug && (
                                        <Button asChild variant="link" size="sm" className="pl-0 text-primary hover:text-accent">
                                            <Link to={`/locations/${activity.locationSlug}`}>
                                                Explore Location <ArrowRight className="ml-2 h-4 w-4" />
                                            </Link>
                                        </Button>
                                    )}
                                    {activity.coordinates && activity.coordinates.lat !== 0 && (
                                        <Button asChild variant="link" size="sm" className="pl-0 text-primary hover:text-accent">
                                            <a href={`https://www.google.com/maps/search/?api=1&query=${activity.coordinates.lat},${activity.coordinates.lng}`} target="_blank" rel="noopener noreferrer">
                                                Navigate <MapPin className="ml-2 h-4 w-4" />
                                            </a>
                                        </Button>
                                    )}
                                </div>
                                {isEditable && (
                                    <div className="flex items-center gap-1">
                                        <ActivityEditor
                                            activity={activity}
                                            onSave={(updatedActivity) => onUpdateActivity(dayData.day, index, updatedActivity)}
                                            triggerButton={
                                                <Button variant="ghost" size="icon" className="hover:bg-primary/10 hover:text-primary">
                                                    <Edit className="h-4 w-4" />
                                                </Button>
                                            }
                                        />
                                        <AlertDialog>
                                            <AlertDialogTrigger asChild>
                                                <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive hover:bg-destructive/10">
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </AlertDialogTrigger>
                                            <AlertDialogContent>
                                                <AlertDialogHeader>
                                                    <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                                                    <AlertDialogDescription>
                                                        This will permanently delete the activity: "{activity.locationName}".
                                                    </AlertDialogDescription>
                                                </AlertDialogHeader>
                                                <AlertDialogFooter>
                                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                    <AlertDialogAction onClick={() => onDeleteActivity(dayData.day, index)}>
                                                        Delete
                                                    </AlertDialogAction>
                                                </AlertDialogFooter>
                                            </AlertDialogContent>
                                        </AlertDialog>
                                    </div>
                                )}
                            </div>
                        </AccordionContent>
                    </AccordionItem>
                </Accordion>
            ))}
        </div>
    )

    const AccommodationPlanner = (
        <div className="mt-8">
            <Separator className="mb-6" />
            <div className="pt-2">
                <CardHeader className="p-0 mb-6">
                    <CardTitle className="flex items-center gap-2 text-lg">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center ring-2 ring-primary/20">
                            <Hotel className="h-5 w-5 text-primary" />
                        </div>
                        <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">Accommodation for Day {dayData.day}</span>
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    <div className="flex items-center justify-between p-4 bg-gradient-to-r from-muted/50 to-muted/30 rounded-xl border border-border/50">
                        <p className="font-semibold">Do you have accommodation booked for this night?</p>
                        <div className="flex gap-2">
                            <Button
                                size="sm"
                                variant={accommodationAnswer === 'yes' ? 'default' : 'outline'}
                                onClick={() => setAccommodationAnswer('yes')}
                                className={accommodationAnswer === 'yes' ? 'bg-gradient-to-r from-primary to-primary/90' : 'hover:border-primary hover:bg-primary/5'}
                            >
                                Yes
                            </Button>
                            <Button
                                size="sm"
                                variant={accommodationAnswer === 'no' ? 'default' : 'outline'}
                                onClick={() => setAccommodationAnswer('no')}
                                className={accommodationAnswer === 'no' ? 'bg-gradient-to-r from-primary to-primary/90' : 'hover:border-primary hover:bg-primary/5'}
                            >
                                No
                            </Button>
                        </div>
                    </div>
                    {accommodationAnswer === 'yes' && (
                        <div className="mt-4 text-center">
                            <ActivityEditor
                                onSave={(activity) => {
                                    onAddActivity(dayData.day, activity);
                                }}
                                triggerButton={
                                    <Button variant="default" className="bg-gradient-to-r from-primary to-primary/90 hover:shadow-loch-md transition-smooth">
                                        <Hotel className="mr-2 h-4 w-4" /> Add Lodging Details
                                    </Button>
                                }
                            />
                        </div>
                    )}
                    {accommodationAnswer === 'no' && (
                        <div className="mt-4 text-center">
                            <Button asChild className="bg-gradient-to-r from-primary to-primary/90 hover:shadow-loch-md transition-smooth">
                                <Link to="/accommodation/search">
                                    <Search className="mr-2 h-4 w-4" /> Find Accommodation
                                </Link>
                            </Button>
                        </div>
                    )}
                </CardContent>
            </div>
        </div>
    );

    return (
        <Card>
            <CardHeader>
                <div className="flex justify-between items-start">
                    <div className="flex-grow">
                        {isEditingTitle && isEditable ? (
                            <div className="flex items-center gap-2">
                                <Input
                                    ref={titleInputRef}
                                    value={editingTitle}
                                    onChange={(e) => setEditingTitle(e.target.value)}
                                    onBlur={handleTitleSave}
                                    onKeyDown={handleTitleKeyDown}
                                    className="text-2xl font-semibold h-auto p-1"
                                />
                            </div>
                        ) : (
                            <div className="flex items-center gap-3 group">
                                <CardTitle>{dayData.title}</CardTitle>
                                {isEditable && (
                                    <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100" onClick={() => setIsEditingTitle(true)}>
                                        <Edit className="h-4 w-4" />
                                    </Button>
                                )}
                            </div>
                        )}
                        <CardDescription className="flex items-center gap-2 mt-1">
                            {format(dayDate, 'PPPP')}
                        </CardDescription>
                    </div>
                    {isEditable && onDateChange && (
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button variant="outline" size="sm" className="gap-2">
                                    <CalendarIcon className="h-4 w-4" />
                                    Change Date
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0">
                                <Calendar
                                    mode="single"
                                    selected={newDate}
                                    onSelect={handleDateChange}
                                    initialFocus
                                />
                            </PopoverContent>
                        </Popover>
                    )}
                </div>
            </CardHeader>
            <CardContent>
                {ActivityListContent}
                {isEditable && (
                    <div className="mt-6 flex justify-center">
                        <ActivityEditor
                            onSave={(newActivity) => onAddActivity(dayData.day, newActivity)}
                            triggerButton={
                                <Button variant="default">
                                    <Plus className="mr-2 h-4 w-4" /> Add Activity
                                </Button>
                            }
                        />
                    </div>
                )}
                {isEditable && !hasLodging && AccommodationPlanner}
                {isEditable && DayNotesEditor}
            </CardContent>
        </Card>
    );
}
